import { useEffect } from "react";
import { Link } from "react-router-dom";
import { MapPin, PlusCircle, ShieldCheck } from "lucide-react";
import { EditorialPage, handwritten } from "../components/editorial";
import MeshField from "../components/story/MeshField";
import Reveal from "../components/story/Reveal";
import Doodle from "../components/story/Doodle";
import Highlighter from "../components/story/Highlighter";
import AppDownload from "../components/AppDownload";

const features = [
  {
    Icon: MapPin,
    title: "A map of right-now",
    body: "Spontaneous loops within a 10-minute walk: coffees, walks, dinners, runs.",
  },
  {
    Icon: PlusCircle,
    title: "Post one in seconds",
    body: "Add a photo, drop a pin, say what time. Three taps and the right people loop in.",
  },
  {
    Icon: ShieldCheck,
    title: "Real people, by default",
    body: "Real names, real photos, a history you can see. Identity is the trust layer.",
  },
];

const DownloadPage = () => {
  useEffect(() => {
    document.title = "Download LetsLoop for iOS & Android";
  }, []);

  return (
    <EditorialPage>
      <div className="relative overflow-hidden">
        <MeshField variant="ambient" />
        <div aria-hidden="true" className="absolute inset-0">
          <Doodle
            name="arrow"
            size={62}
            rotate={-16}
            className="absolute left-[4%] top-[30%] text-teal/40 story-float"
          />
          <Doodle
            name="starFilled"
            size={24}
            className="absolute right-[6%] top-[18%] text-salmon/60 story-float"
            style={{ animationDelay: "-2s" }}
          />
          <Doodle
            name="burst"
            size={38}
            className="absolute left-[16%] bottom-[10%] text-salmon/60 story-float"
            style={{ animationDelay: "-3.4s" }}
          />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 md:px-12 pt-12 pb-24">
          {/* top bar (pages have no global navbar) */}
          <div className="flex items-center justify-between mb-12">
            <Link to="/" aria-label="LetsLoop home">
              <img
                src="/letsloop-logo.png"
                alt="LetsLoop"
                className="h-7 md:h-8 w-auto"
              />
            </Link>
            <Link
              to="/how-it-works"
              className="text-sm font-semibold text-ink/70 hover:text-salmon transition-colors"
            >
              How it works &rarr;
            </Link>
          </div>

          {/* hero: download-forward, with the actual app on the right (not a lifestyle photo) */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
            <div>
              <p
                className="story-enter text-3xl md:text-4xl text-salmon-dark mb-3 -rotate-2 origin-left"
                style={handwritten}
              >
                get the app
              </p>
              <h1
                className="story-enter font-headline text-ink tracking-[-0.03em] leading-[1.06] text-5xl md:text-6xl mb-5"
                style={{ fontWeight: 700 }}
              >
                LetsLoop,{" "}
                <Highlighter tone="coral">in your pocket.</Highlighter>
              </h1>
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed max-w-md mb-9">
                Scan the code with your phone, or grab it from the App Store or
                Google Play. Spontaneous plans with real people, near you.
              </p>
              <AppDownload showQr showTrust placement="download-top" />
            </div>

            {/* Current app screenshots, captured 2026-07-31, in real device
                frames. These replace /screen-myloops.png and /screen-loops.png,
                which showed an older build of the app.

                The bezel, corner radius and Dynamic Island are baked INTO the
                image rather than drawn in CSS, which is why there is no border
                or radius here: the old markup faked a device with a 4px border
                and a hard offset shadow, and a real frame reads as the product
                instead of as a bordered rectangle. iOS screenshots do not
                include the island, so it is drawn back on. */}
            <div className="relative flex justify-center items-end h-[420px] md:h-[520px]">
              <img
                src="/story/app-communities.webp"
                srcSet="/story/app-communities-sm.webp 420w, /story/app-communities.webp 760w"
                sizes="(max-width: 768px) 40vw, 210px"
                alt="The LetsLoop app showing communities you can join in London"
                className="absolute right-1/2 mr-2 bottom-2 w-40 md:w-52 -rotate-6 drop-shadow-[0_22px_40px_rgba(45,56,73,0.3)]"
                loading="lazy"
              />
              <img
                src="/story/app-map.webp"
                srcSet="/story/app-map-sm.webp 420w, /story/app-map.webp 760w"
                sizes="(max-width: 768px) 44vw, 230px"
                alt="The LetsLoop app map showing loops happening near you across London"
                className="relative z-10 w-44 md:w-56 rotate-2 drop-shadow-[0_26px_50px_rgba(45,56,73,0.36)]"
                loading="lazy"
              />
            </div>
          </div>

          {/* what's inside the app */}
          <div className="grid md:grid-cols-3 gap-6 mt-24">
            {features.map(({ Icon, title, body }) => (
              <Reveal
                key={title}
                y={24}
                className="bg-canvas border border-hairline rounded-3xl p-7 shadow-[0_18px_44px_-20px_rgba(45,56,73,0.3)]"
              >
                <Icon className="w-7 h-7 text-salmon mb-4" strokeWidth={2} />
                <h2
                  className="font-headline text-2xl text-ink mb-2 tracking-[-0.02em]"
                  style={{ fontWeight: 700 }}
                >
                  {title}
                </h2>
                <p className="text-ink/70 leading-relaxed">{body}</p>
              </Reveal>
            ))}
          </div>

          {/* download-focused close (no homepage motifs) */}
        </div>
      </div>

      <section className="relative w-full ground-teal py-20 md:py-28 px-6 md:px-12 overflow-hidden">
        <Doodle
          name="squiggle"
          size={96}
          rotate={-6}
          className="absolute w-8 h-8 md:w-auto md:h-auto right-[6%] top-[16%] text-salmon/40"
        />
        <Doodle
          name="loop"
          size={70}
          className="absolute w-8 h-8 md:w-auto md:h-auto left-[6%] bottom-[14%] text-[#f9f9f7]/20"
        />
        <Reveal y={26} className="relative z-10">
          <div className="max-w-3xl mx-auto flex flex-col items-center text-center">
            <h2
              className="font-headline text-[#f9f9f7] text-3xl md:text-5xl mb-3 tracking-[-0.03em] leading-[1.14]"
              style={{ fontWeight: 700 }}
            >
              Get LetsLoop, <Highlighter tone="coral">free.</Highlighter>
            </h2>
            <p className="text-lg text-[#f9f9f7]/75 mb-8 max-w-md">
              iOS and Android. London-wide. Two taps from your next plan.
            </p>
            <AppDownload
              showQr
              align="center"
              tone="light"
              placement="download-bottom"
            />
          </div>
        </Reveal>
      </section>
    </EditorialPage>
  );
};

export default DownloadPage;
